import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  isIOSDevice,
  isAndroidDevice,
  isMobileDevice,
  isHTTPS,
  checkCameraPermissionStatus,
  requestCameraPermission,
  getIOSErrorMessage
} from '../utils/iosHelpers';

const CameraTest = () => {
  const [deviceInfo, setDeviceInfo] = useState({});
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const [isRequesting, setIsRequesting] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    // Cihaz bilgilerini topla
    const info = {
      isIOS: isIOSDevice(),
      isAndroid: isAndroidDevice(),
      isMobile: isMobileDevice(),
      isHTTPS: isHTTPS(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      mediaDevices: !!navigator.mediaDevices,
      getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      permissions: !!navigator.permissions
    };
    setDeviceInfo(info);

    // Kamera izni durumunu kontrol et
    checkPermissionStatus();

    // Test sonuçlarını topla
    runTests();
  }, []);

  const checkPermissionStatus = async () => {
    try {
      setPermissionStatus('checking');
      const status = await checkCameraPermissionStatus();
      setPermissionStatus(status);
      console.log('Kamera izni durumu:', status);
    } catch (error) {
      console.error('İzin durumu kontrolü hatası:', error);
      setPermissionStatus('error');
      setError(error.message);
    }
  };

  const requestPermission = async () => {
    try {
      setIsRequesting(true);
      setError(null);
      
      console.log('Kamera izni isteniyor...');
      await requestCameraPermission();
      
      setPermissionStatus('granted');
      console.log('Kamera izni verildi');
      
    } catch (error) {
      console.error('İzin isteme hatası:', error);
      setPermissionStatus('denied');
      setError(getIOSErrorMessage(error));
    } finally {
      setIsRequesting(false);
    }
  };

  const runTests = () => {
    const results = {
      // Temel özellikler
      mediaDevices: !!navigator.mediaDevices,
      getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      permissions: !!navigator.permissions,
      
      // Kamera özellikleri
      facingMode: true, // Arka kamera desteği
      constraints: !!navigator.mediaDevices?.getSupportedConstraints,
      
      // Güvenlik
      secureContext: window.isSecureContext,
      https: isHTTPS(),
      
      // Mobil özellikler
      touch: 'ontouchstart' in window,
      orientation: 'orientation' in window
    };

    setTestResults(results);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'granted': return '✅';
      case 'denied': return '❌';
      case 'prompt': return '⚠️';
      case 'checking': return '⏳';
      case 'error': return '💥';
      default: return '❓';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'granted': return 'İzin Verildi';
      case 'denied': return 'İzin Reddedildi';
      case 'prompt': return 'İzin İstenebilir';
      case 'checking': return 'Kontrol Ediliyor...';
      case 'error': return 'Hata';
      default: return 'Bilinmiyor';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'granted': return 'green';
      case 'denied': return 'red';
      case 'prompt': return 'yellow';
      case 'checking': return 'blue';
      case 'error': return 'red';
      default: return 'gray';
    }
  };

  const getPermissionMessage = () => {
    if (deviceInfo.isIOS && !deviceInfo.isHTTPS) {
      return 'iOS cihazlarda kamera erişimi için HTTPS bağlantısı gereklidir.';
    }
    
    switch (permissionStatus) {
      case 'granted':
        return 'Kamera izni verildi. QR kod tarayıcı kullanabilirsiniz.';
      case 'denied':
        return 'Kamera erişimi reddedildi. Tarayıcı ayarlarından izni kontrol edin.';
      case 'prompt':
        return 'Kamera izni istenebilir. İzin verdiğinizde arka kamera kullanılacaktır.';
      case 'checking':
        return 'Kamera izni durumu kontrol ediliyor...';
      case 'error':
        return 'Kamera izni kontrolünde hata oluştu.';
      default:
        return 'Kamera izni durumu bilinmiyor.';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📷 Kamera İzni Test Sayfası</h1>
          <p className="text-gray-600">Mobil cihazlarda kamera izni ve arka kamera kullanımını test edin</p>
        </div>

        {/* Navigation */}
        <div className="mb-6">
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            ← Ana Sayfaya Dön
          </Link>
        </div>

        {/* Cihaz Bilgileri */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📋 Cihaz Bilgileri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Temel Bilgiler</h3>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Platform:</span> {deviceInfo.platform}</div>
                <div><span className="font-medium">iOS:</span> {deviceInfo.isIOS ? 'Evet' : 'Hayır'}</div>
                <div><span className="font-medium">Android:</span> {deviceInfo.isAndroid ? 'Evet' : 'Hayır'}</div>
                <div><span className="font-medium">Mobil:</span> {deviceInfo.isMobile ? 'Evet' : 'Hayır'}</div>
                <div><span className="font-medium">HTTPS:</span> {deviceInfo.isHTTPS ? 'Evet' : 'Hayır'}</div>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Kamera Desteği</h3>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">MediaDevices:</span> {testResults.mediaDevices ? '✅' : '❌'}</div>
                <div><span className="font-medium">getUserMedia:</span> {testResults.getUserMedia ? '✅' : '❌'}</div>
                <div><span className="font-medium">Permissions API:</span> {testResults.permissions ? '✅' : '❌'}</div>
                <div><span className="font-medium">Arka Kamera:</span> {testResults.facingMode ? '✅' : '❌'}</div>
                <div><span className="font-medium">Güvenli Bağlam:</span> {testResults.secureContext ? '✅' : '❌'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Kamera İzni Durumu */}
        <div className={`bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-${getStatusColor(permissionStatus)}-500`}>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🔐 Kamera İzni Durumu</h2>
          
          <div className="flex items-center mb-4">
            <span className="text-2xl mr-3">{getStatusIcon(permissionStatus)}</span>
            <div>
              <h3 className="font-medium text-gray-900">{getStatusText(permissionStatus)}</h3>
              <p className="text-sm text-gray-600">{getPermissionMessage()}</p>
            </div>
          </div>

          {/* İzin İsteme Butonu */}
          {permissionStatus === 'prompt' && (
            <button
              onClick={requestPermission}
              disabled={isRequesting}
              className="w-full btn-primary py-3"
            >
              {isRequesting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  İzin İsteniyor...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  </svg>
                  Kamera İzni İste
                </>
              )}
            </button>
          )}

          {/* Yenile Butonu */}
          {permissionStatus !== 'checking' && (
            <button
              onClick={checkPermissionStatus}
              className="w-full mt-3 btn-secondary py-2"
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Durumu Yenile
            </button>
          )}
        </div>

        {/* Hata Mesajı */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Test Sonuçları */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🧪 Kamera Test Sonuçları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">API Desteği</h3>
              <div className="space-y-1 text-sm">
                <div>{testResults.mediaDevices ? '✅' : '❌'} MediaDevices API</div>
                <div>{testResults.getUserMedia ? '✅' : '❌'} getUserMedia</div>
                <div>{testResults.permissions ? '✅' : '❌'} Permissions API</div>
                <div>{testResults.constraints ? '✅' : '❌'} Constraints API</div>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Güvenlik</h3>
              <div className="space-y-1 text-sm">
                <div>{testResults.secureContext ? '✅' : '❌'} Secure Context</div>
                <div>{testResults.https ? '✅' : '❌'} HTTPS Bağlantısı</div>
                <div>{testResults.facingMode ? '✅' : '❌'} Arka Kamera</div>
                <div>{testResults.touch ? '✅' : '❌'} Dokunmatik Ekran</div>
              </div>
            </div>
          </div>
        </div>

        {/* iOS Özel Bilgiler */}
        {deviceInfo.isIOS && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">🍎 iOS Özel Bilgiler</h2>
            <div className="space-y-2 text-sm text-blue-800">
              <div>• Safari tarayıcısı önerilir</div>
              <div>• Kamera erişimi için HTTPS zorunlu</div>
              <div>• İlk kullanımda izin istenecek</div>
              <div>• Arka kamera varsayılan olarak kullanılır</div>
              <div>• İzin reddedilirse Safari ayarlarından kontrol edin</div>
            </div>
          </div>
        )}

        {/* Android Özel Bilgiler */}
        {deviceInfo.isAndroid && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-green-900 mb-4">🤖 Android Özel Bilgiler</h2>
            <div className="space-y-2 text-sm text-green-800">
              <div>• Chrome tarayıcısı önerilir</div>
              <div>• Kamera erişimi desteklenir</div>
              <div>• İzin isteği otomatik gösterilir</div>
              <div>• Arka kamera varsayılan olarak kullanılır</div>
              <div>• Site ayarlarından izin kontrolü yapılabilir</div>
            </div>
          </div>
        )}

        {/* Sorun Giderme */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-yellow-900 mb-4">🛠️ Sorun Giderme</h2>
          <div className="space-y-2 text-sm text-yellow-800">
            <div><strong>İzin Reddedildi:</strong> Tarayıcı ayarlarından kamera iznini kontrol edin</div>
            <div><strong>iOS HTTPS Hatası:</strong> HTTPS bağlantısı kullandığınızdan emin olun</div>
            <div><strong>Kamera Açılmıyor:</strong> Başka uygulamaların kamerayı kullanmadığından emin olun</div>
            <div><strong>Arka Kamera Çalışmıyor:</strong> Cihazınızda arka kamera olduğundan emin olun</div>
          </div>
        </div>

        {/* Test Linkleri */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-700 mb-2">Test Linkleri</h3>
          <div className="space-y-2 text-sm">
            <Link to="/mobile-test" className="text-blue-600 hover:text-blue-800 block">
              📱 Mobil Cihaz Test Sayfası
            </Link>
            <Link to="/media-test" className="text-blue-600 hover:text-blue-800 block">
              🎵 Medya Test Sayfası
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraTest; 